<template>
  <Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
    <DialogScrollContent class="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>Crop Photo</DialogTitle>
        <DialogDescription>
          Adjust your photo to fit the profile picture requirements.
        </DialogDescription>
      </DialogHeader>
      <div class="h-[400px] bg-gray-50 rounded-lg overflow-hidden">
        <img
          v-if="imageUrl"
          ref="imageRef"
          :src="imageUrl"
          class="block max-w-full"
        />
      </div>
      <DialogFooter class="flex justify-end gap-2">
        <Button variant="outline" @click="$emit('update:isOpen', false)">
          Cancel
        </Button>
        <Button @click="handleCrop" :disabled="isLoading">
          <Loader2 v-if="isLoading" class="mr-2 h-4 w-4 animate-spin" />
          Save
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { Loader2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogScrollContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const props = defineProps<{
  isOpen: boolean
  imageUrl: string
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'crop', value: string): void
}>()

const isLoading = ref(false)
const imageRef = ref<HTMLImageElement>()
let cropper: Cropper | null = null

function destroyCropper() {
  if (cropper) {
    cropper.destroy()
    cropper = null
  }
}

function initCropper() {
  destroyCropper()
  if (!imageRef.value) return
  cropper = new Cropper(imageRef.value, {
    aspectRatio: 1,
    viewMode: 1,
    dragMode: 'move',
    autoCropArea: 1,
    responsive: true,
    background: false,
  })
}

watch(() => props.isOpen, async (open) => {
  if (open) {
    await nextTick()
    setTimeout(() => {
      initCropper()
    }, 300)
  } else {
    destroyCropper()
  }
})

onBeforeUnmount(() => {
  destroyCropper()
})

const handleCrop = async () => {
  if (!cropper) return

  isLoading.value = true
  try {
    const canvas = cropper.getCroppedCanvas({
      width: 480,
      height: 480,
    })

    const base64 = canvas.toDataURL('image/jpeg', 0.9)
    emit('crop', base64)
    emit('update:isOpen', false)
  } catch (error) {
    console.error('Error cropping image:', error)
  } finally {
    isLoading.value = false
  }
}
</script>
